import { ticketingFetch } from "./client";

export type Company = {
  id: number;
  name: string;
  type: string;
  is_active: boolean;
};

export type Department = {
  id: number;
  company_id: number;
  company_name: string | null;
  name: string;
  code: string;
  is_active: boolean;
};

export type RequestType = {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
};

export type CompanyResponse = Company;

export type DepartmentAdminResponse = {
  id: number;
  company_id: number;
  company_name: string | null;
  name: string;
  code: string;
  is_active: boolean;
  sort_order: number;
  is_ticket_routing_enabled: boolean;
};

export type DepartmentUsageSummary = {
  department_id: number;
  active_users: number;
  active_users_outside_blocking: boolean;
  active_routing_request_types: number;
  open_tickets: number;
  total_tickets: number;
};

export type RequestTypeAdminResponse = {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  code: string;
  default_department_id: number | null;
  default_department_name: string | null;
  is_internal_visible: boolean;
  is_customer_visible: boolean;
  is_partner_visible: boolean;
};

export type RequestTypeUsageSummary = {
  request_type_id: number;
  active_tickets: number;
  open_tickets: number;
  total_tickets: number;
  active_default_department: boolean;
  default_department_routing_enabled: boolean;
};

export type AdminFieldError = {
  location: ReadonlyArray<string | number>;
  message: string;
  type: string | null;
};

export class AdminApiError extends Error {
  status: number;
  errorCode: string | null;
  detail: string;
  fieldErrors: ReadonlyArray<AdminFieldError>;

  constructor(
    status: number,
    detail: string,
    errorCode: string | null,
    fieldErrors: ReadonlyArray<AdminFieldError>,
  ) {
    super(detail);
    this.name = "AdminApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.detail = detail;
    this.fieldErrors = fieldErrors;
  }
}

export type ManagedUser = {
  id: number;
  full_name: string;
  email: string;
  company_id: number;
  company_name: string | null;
  department_id: number | null;
  department_name: string | null;
  role: "Requester" | "Staff" | "Admin";
  is_active: boolean;
  avatar_url: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type UserCreatePayload = {
  full_name: string;
  email: string;
  company_id: number;
  department_id: number | null;
  role: "Requester" | "Staff" | "Admin";
  temporary_password: string;
};

export type UserUpdatePayload = Pick<
  ManagedUser,
  "full_name" | "department_id" | "role" | "is_active"
>;

export type UserImportCandidate = {
  full_name: string;
  email: string;
  role: "Requester" | "Staff" | "Admin";
  department: string | null;
};

export type UserImportRowResult = UserImportCandidate & {
  row_number: number;
  status: "ready" | "error" | "conflict";
  errors: string[];
  temporary_password: string | null;
};

export type UserImportResponse = {
  results: UserImportRowResult[];
};

type ApiRequestOptions = {
  body?: string;
  headers?: Record<string, string>;
  method?: string;
};

const SAFE_NETWORK_MESSAGE = "Unable to connect to the server. Please check your connection and try again.";

const FALLBACK_ADMIN_MESSAGES: Record<number, string> = {
  400: "The request was invalid.",
  401: "You are not authenticated. Please sign in again.",
  403: "You are not allowed to perform this action.",
  404: "The requested item was not found.",
  409: "The request could not be completed because of a conflict.",
  422: "The submitted values are invalid. Please review the highlighted fields.",
  500: "The server encountered an error. Please try again.",
};

function adminFallbackMessage(status: number): string {
  const explicit = FALLBACK_ADMIN_MESSAGES[status];
  if (explicit) {
    return explicit;
  }
  if (status >= 500) {
    return "The server encountered an error. Please try again.";
  }
  if (status >= 400) {
    return "The request could not be completed.";
  }
  return SAFE_NETWORK_MESSAGE;
}

function adminIsRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function adminReadFieldErrors(body: unknown): ReadonlyArray<AdminFieldError> {
  if (!adminIsRecord(body)) {
    return [];
  }
  const detail = body.detail;
  if (!Array.isArray(detail)) {
    return [];
  }
  const collected: AdminFieldError[] = [];
  for (const entry of detail) {
    if (!adminIsRecord(entry)) {
      continue;
    }
    const message =
      typeof entry.msg === "string" && entry.msg.length > 0
        ? entry.msg
        : "";
    if (message.length === 0) {
      continue;
    }
    const type =
      typeof entry.type === "string" && entry.type.length > 0
        ? entry.type
        : null;
    const rawLoc = entry.loc;
    const location: ReadonlyArray<string | number> = Array.isArray(rawLoc)
      ? rawLoc.flatMap((segment): Array<string | number> => {
          if (typeof segment === "string") {
            return [segment];
          }
          if (typeof segment === "number" && Number.isFinite(segment)) {
            return [segment];
          }
          return [];
        })
      : typeof rawLoc === "string"
        ? [rawLoc]
        : [];
    collected.push({
      location: Object.freeze(location),
      message,
      type,
    });
  }
  return collected;
}

async function adminSafeReadJson(response: Response): Promise<unknown | null> {
  if (response.status === 204) {
    return null;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    return null;
  }
  const text = await response.text();
  if (text.length === 0) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function buildAdminApiError(response: Response): Promise<AdminApiError> {
  const status = response.status;
  const body = await adminSafeReadJson(response);
  const fallback = adminFallbackMessage(status);

  if (adminIsRecord(body)) {
    const detailField = body.detail;
    if (typeof detailField === "string" && detailField.length > 0) {
      const codeField = body.error_code;
      const code = typeof codeField === "string" && codeField.length > 0 ? codeField : null;
      const fieldErrors = adminReadFieldErrors(body);
      return new AdminApiError(status, detailField, code, fieldErrors);
    }
    if (Array.isArray(detailField)) {
      const fieldErrors = adminReadFieldErrors(body);
      const firstMessage = fieldErrors.find((entry) => entry.message.length > 0)?.message;
      const aggregate = firstMessage
        ? `Validation failed: ${firstMessage}`
        : fallback;
      return new AdminApiError(status, aggregate, null, fieldErrors);
    }
    const messageField = body.message;
    if (typeof messageField === "string" && messageField.length > 0) {
      return new AdminApiError(status, messageField, null, []);
    }
  }
  if (typeof body === "string" && body.length > 0) {
    return new AdminApiError(status, body, null, []);
  }
  return new AdminApiError(status, fallback, null, []);
}

async function request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await ticketingFetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed with ${response.status}`);
  }

  return (await response.json()) as T;
}

export function listUsers(): Promise<ManagedUser[]> {
  return request<ManagedUser[]>("/users");
}

export function listDepartmentUsers(): Promise<ManagedUser[]> {
  return request<ManagedUser[]>("/users/department");
}

export function createUser(payload: UserCreatePayload): Promise<ManagedUser> {
  return request<ManagedUser>("/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateUser(userId: number, payload: Partial<UserUpdatePayload>): Promise<ManagedUser> {
  return request<ManagedUser>(`/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function previewUserImport(file: File): Promise<UserImportResponse> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await ticketingFetch("/users/import/preview", {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    throw await buildAdminApiError(response);
  }
  return (await response.json()) as UserImportResponse;
}

export function confirmUserImport(rows: UserImportCandidate[]): Promise<UserImportResponse> {
  return adminRequest<UserImportResponse>("/users/import/confirm", {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

export function deactivateUser(userId: number): Promise<ManagedUser> {
  return request<ManagedUser>(`/users/${userId}/deactivate`, {
    method: "POST",
  });
}

export type UserPasswordResetResponse = {
  user_id: number;
  email: string;
  temporary_password: string;
};

export function resetUserPassword(userId: number): Promise<UserPasswordResetResponse> {
  return request<UserPasswordResetResponse>(`/users/${userId}/reset-password`, {
    method: "POST",
  });
}

export function listCompanies(): Promise<CompanyResponse[]> {
  return request<CompanyResponse[]>("/companies");
}

export function listDepartments(): Promise<Department[]> {
  return request<Department[]>("/departments");
}

export function listRoutingDepartments(): Promise<Department[]> {
  return request<Department[]>("/departments?routing_only=true");
}

export function listRequestTypes(): Promise<RequestType[]> {
  return request<RequestType[]>("/request-types");
}

function buildIncludeInactiveQuery(includeInactive: boolean | undefined): string {
  if (includeInactive) {
    return "?include_inactive=true";
  }
  return "";
}

async function adminRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const response = await ticketingFetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw await buildAdminApiError(response);
  }

  const body = await adminSafeReadJson(response);
  if (body === null) {
    throw new AdminApiError(
      response.status,
      adminFallbackMessage(response.status),
      null,
      [],
    );
  }
  return body as T;
}

export function listAdminDepartments(
  includeInactive?: boolean,
): Promise<DepartmentAdminResponse[]> {
  return adminRequest<DepartmentAdminResponse[]>(
    `/api/admin/master-data/departments${buildIncludeInactiveQuery(includeInactive)}`,
  );
}

export function getAdminDepartment(
  departmentId: number,
): Promise<DepartmentAdminResponse> {
  return adminRequest<DepartmentAdminResponse>(
    `/api/admin/master-data/departments/${departmentId}`,
  );
}

export function getAdminDepartmentUsage(
  departmentId: number,
): Promise<DepartmentUsageSummary> {
  return adminRequest<DepartmentUsageSummary>(
    `/api/admin/master-data/departments/${departmentId}/usage`,
  );
}

export function listAdminRequestTypes(
  includeInactive?: boolean,
): Promise<RequestTypeAdminResponse[]> {
  return adminRequest<RequestTypeAdminResponse[]>(
    `/api/admin/master-data/request-types${buildIncludeInactiveQuery(includeInactive)}`,
  );
}

export function getAdminRequestType(
  requestTypeId: number,
): Promise<RequestTypeAdminResponse> {
  return adminRequest<RequestTypeAdminResponse>(
    `/api/admin/master-data/request-types/${requestTypeId}`,
  );
}

export function getAdminRequestTypeUsage(
  requestTypeId: number,
): Promise<RequestTypeUsageSummary> {
  return adminRequest<RequestTypeUsageSummary>(
    `/api/admin/master-data/request-types/${requestTypeId}/usage`,
  );
}

export type DepartmentCreatePayload = {
  company_id: number;
  name: string;
  code: string;
  sort_order?: number;
  is_ticket_routing_enabled?: boolean;
};

export type DepartmentUpdatePayload = {
  name?: string | null;
  sort_order?: number | null;
  is_ticket_routing_enabled?: boolean | null;
};

export type RequestTypeCreatePayload = {
  code: string;
  name: string;
  description?: string | null;
  sort_order?: number;
  default_department_id?: number | null;
  is_internal_visible?: boolean;
  is_customer_visible?: boolean;
  is_partner_visible?: boolean;
};

export type RequestTypeUpdatePayload = {
  name?: string | null;
  description?: string | null;
  sort_order?: number | null;
  default_department_id?: number | null;
  is_internal_visible?: boolean | null;
  is_customer_visible?: boolean | null;
  is_partner_visible?: boolean | null;
};

export function createAdminDepartment(
  payload: DepartmentCreatePayload,
): Promise<DepartmentAdminResponse> {
  return adminRequest<DepartmentAdminResponse>("/api/admin/master-data/departments", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAdminDepartment(
  departmentId: number,
  payload: DepartmentUpdatePayload,
): Promise<DepartmentAdminResponse> {
  return adminRequest<DepartmentAdminResponse>(
    `/api/admin/master-data/departments/${departmentId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deactivateAdminDepartment(
  departmentId: number,
): Promise<DepartmentAdminResponse> {
  return adminRequest<DepartmentAdminResponse>(
    `/api/admin/master-data/departments/${departmentId}/deactivate`,
    {
      method: "POST",
    },
  );
}

export function reactivateAdminDepartment(
  departmentId: number,
): Promise<DepartmentAdminResponse> {
  return adminRequest<DepartmentAdminResponse>(
    `/api/admin/master-data/departments/${departmentId}/reactivate`,
    {
      method: "POST",
    },
  );
}

export function createAdminRequestType(
  payload: RequestTypeCreatePayload,
): Promise<RequestTypeAdminResponse> {
  return adminRequest<RequestTypeAdminResponse>(
    "/api/admin/master-data/request-types",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function updateAdminRequestType(
  requestTypeId: number,
  payload: RequestTypeUpdatePayload,
): Promise<RequestTypeAdminResponse> {
  return adminRequest<RequestTypeAdminResponse>(
    `/api/admin/master-data/request-types/${requestTypeId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deactivateAdminRequestType(
  requestTypeId: number,
): Promise<RequestTypeAdminResponse> {
  return adminRequest<RequestTypeAdminResponse>(
    `/api/admin/master-data/request-types/${requestTypeId}/deactivate`,
    {
      method: "POST",
    },
  );
}

export function reactivateAdminRequestType(
  requestTypeId: number,
): Promise<RequestTypeAdminResponse> {
  return adminRequest<RequestTypeAdminResponse>(
    `/api/admin/master-data/request-types/${requestTypeId}/reactivate`,
    {
      method: "POST",
    },
  );
}

export type RequestTypeDefaultDepartmentPayload = {
  department_id: number | null;
};

export function updateAdminRequestTypeDefaultDepartment(
  requestTypeId: number,
  payload: RequestTypeDefaultDepartmentPayload,
): Promise<RequestTypeAdminResponse> {
  return adminRequest<RequestTypeAdminResponse>(
    `/api/admin/master-data/request-types/${requestTypeId}/default-department`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export type RequestTypeAudiencePayload = {
  is_internal_visible: boolean;
  is_customer_visible: boolean;
  is_partner_visible: boolean;
};

export function updateAdminRequestTypeAudience(
  requestTypeId: number,
  payload: RequestTypeAudiencePayload,
): Promise<RequestTypeAdminResponse> {
  return adminRequest<RequestTypeAdminResponse>(
    `/api/admin/master-data/request-types/${requestTypeId}/audience`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}
